import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {$host} from "../../http/index";
import useSnackbar from "../../hooks/useSnackbar";

// Подключаем плагины
dayjs.extend(utc);
dayjs.extend(timezone);

const TriggerForm = ({editingId, onCloseModal, availableBranches}) => {
    const typeLabels = {
        REGULAR_PATIENT: "Постоянный клиент",
        FIRST_APPOINTMENT: "После первого приема",
        DATE: "Конкретная дата",
        INACTIVE_PATIENT: "Неактивный пациент",
        BIRTHDAY: "День рождения",
        REMIND_APPOINTMENT: "Напоминание о завтрашнем приеме",
        DRINKS_BEFORE_APPOINTMENT: "Предложение напитков перед приемом",
        PROMO_END: "Напоминание об окончании акций",
    };

    const [templateText, setTemplateText] = useState("");
    const [type, setType] = useState(null);
    const [newImageFile, setNewImageFile] = useState(null);
    const [branchId, setBranchId] = useState("");
    const [sendToAllBranches, setSendToAllBranches] = useState(false);
    const [isRepeatable, setRepeatable] = useState(false);
    const [givePoints, setGivePoints] = useState(false);
    const [points, setPoints] = useState(0);
    const [currentImage, setCurrentImage] = useState(null);
    const [sentAt, setSentAt] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [intervalValue, setIntervalValue] = useState("");
    const [intervalUnit, setIntervalUnit] = useState("DAYS");
    const [nextDate, setNextDate] = useState(null);
    const [daysBefore, setDaysBefore] = useState("");
    const [daysAfterFirstAppointment, setDaysAfterFirstAppointment] = useState("");
    const [daysAfterLastAppointment, setDaysAfterLastAppointment] = useState("");
    const [appointmentsForPeriod, setAppointmentsForPeriod] = useState("");
    const [periodType, setPeriodType] = useState("YEAR");
    const [minutesBeforeAppointment, setMinutesBeforeAppointment] = useState(30);
    const [notifyBeforeDays, setNotifyBeforeDays] = useState(3);
    const [gender, setGender] = useState("");
    const [minAge, setMinAge] = useState("");
    const [maxAge, setMaxAge] = useState("");
    const [timezoneOffset, setTimezoneOffset] = useState(0); // Часовой пояс клиники в минутах

    const snack = useSnackbar();
    const API_URL = process.env.REACT_APP_API_URL || window.API_URL;

    useEffect(() => {
        if (editingId) fetchEventData();
        fetchClinicData();
    }, [editingId]);

    // Получение данных клиники (включая timezoneOffset)
    const fetchClinicData = async () => {
        try {
            const {data} = await $host.get("web/clinic"); // Предполагаемый эндпоинт
            setTimezoneOffset(data.timezoneOffset || 0);
        } catch (error) {
            snack.handleChange(true, "Ошибка загрузки данных клиники", "error");
        }
    };

    const setParamsByType = (type, params) => {
        switch (type) {
            case "DATE":
                if (params.date) {
                    // Интерпретируем дату как локальную для часового пояса клиники
                    const clinicDate = dayjs(params.date).add(timezoneOffset, "minute");
                    setSelectedDate(clinicDate.format("YYYY-MM-DDTHH:mm")); // Формат для datetime-local
                }
                setIntervalValue(params.intervalValue);
                setIntervalUnit(params.intervalUnit);
                setGender(params.gender || "");
                setMinAge(params.minAge ?? "");
                setMaxAge(params.maxAge ?? "");
                break;
            case "BIRTHDAY":
                setDaysBefore(params.daysBefore);
                break;
            case "FIRST_APPOINTMENT":
                setDaysAfterFirstAppointment(params.daysAfterFirstAppointment);
                break;
            case "INACTIVE_PATIENT":
                setDaysAfterLastAppointment(params.daysAfterLastAppointment);
                break;
            case "REGULAR_PATIENT":
                setAppointmentsForPeriod(params.appointmentsForPeriod);
                setPeriodType(params.periodType);
                break;
            case "DRINKS_BEFORE_APPOINTMENT":
                setMinutesBeforeAppointment(params.minutesBeforeAppointments);
                break;
            case "PROMO_END":
                setNotifyBeforeDays(params.notifyBeforeDays);
                break;
            default:
                break;
        }
    };

    const fetchEventData = async () => {
        try {
            const {data} = await $host.get(`web/event_message/${editingId}`);
            setTemplateText(data.templateText);
            setType(data.type);
            setSendToAllBranches(data.sendToAllBranches);
            setBranchId(data.branchId);
            setGivePoints(data.pointsCount > 0);
            setPoints(data.pointsCount);
            setParamsByType(data.type, data.params);
            if (data.imageName)
                setCurrentImage(`${API_URL}/static/${data.imageName}`);
        } catch (error) {
            snack.handleChange(true, "Ошибка загрузки данных триггера", "error");
        }
    };

    const getParamsByType = (type) => {
        switch (type) {
            case "DATE":
                if (selectedDate) {
                    // Формируем дату с указанием часового пояса клиники
                    const localDate = dayjs(selectedDate);
                    // Преобразуем смещение в формат +HH:mm
                    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
                    const offsetMinutes = Math.abs(timezoneOffset) % 60;
                    const offsetSign = timezoneOffset <= 0 ? "+" : "-";
                    const offsetString = `${offsetSign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutes).padStart(2, "0")}`;
                    const formattedDate = localDate.format(`YYYY-MM-DDTHH:mm:ss.SSS${offsetString}`);
                    return {
                        date: formattedDate,
                        isRepeatable,
                        intervalValue,
                        intervalUnit,
                        gender: gender || undefined,
                        minAge: minAge === "" ? undefined : Number(minAge),
                        maxAge: maxAge === "" ? undefined : Number(maxAge),
                    };
                }
                return {
                    intervalValue,
                    intervalUnit,
                    gender: gender || undefined,
                    minAge: minAge === "" ? undefined : Number(minAge),
                    maxAge: maxAge === "" ? undefined : Number(maxAge),
                };
            case "BIRTHDAY":
                return {daysBefore};
            case "FIRST_APPOINTMENT":
                return {daysAfterFirstAppointment};
            case "INACTIVE_PATIENT":
                return {daysAfterLastAppointment};
            case "REGULAR_PATIENT":
                return {appointmentsForPeriod, periodType};
            case "DRINKS_BEFORE_APPOINTMENT":
                return {minutesBeforeAppointment};
            case "PROMO_END":
                return {notifyBeforeDays};
            default:
                return {};
        }
    };

    const validateDateAgeRange = () => {
        if (type !== "DATE") return true;
        if (minAge === "" || maxAge === "") return true;
        return Number(minAge) <= Number(maxAge);
    };

    const handleSave = async () => {
        const formData = new FormData();
        formData.append("title", "Пусто");
        formData.append("templateText", templateText);
        formData.append("type", type);
        formData.append("sendToAllBranches", sendToAllBranches);
        formData.append("params", JSON.stringify(getParamsByType(type)));
        if (!sendToAllBranches) formData.append("branchId", branchId);
        if (givePoints) formData.append("pointsCount", points);
        if (newImageFile) formData.append("image", newImageFile);

        try {
            const response = editingId
                ? await $host.patch(`web/event_message/${editingId}`, formData, {
                    headers: {"Content-Type": "multipart/form-data"},
                })
                : await $host.post("web/event_message", formData, {
                    headers: {"Content-Type": "multipart/form-data"},
                });

            if (newImageFile) {
                setCurrentImage(`${API_URL}/static/${response.data.imageName}`);
            }

            onCloseModal();
            resetForm();
            snack.handleChange(true, "Успешно сохранено", "success");
        } catch (error) {
            snack.handleChange(
                true,
                error.response?.data?.message || "Ошибка сохранения",
                "error"
            );
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            snack.handleChange(true, "Файл должен быть изображением", "error");
            return;
        }

        if (file.size > 3 * 1024 * 1024) {
            snack.handleChange(
                true,
                "Размер файла не должен превышать 3 МБ",
                "error"
            );
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                const MAX_WIDTH = 800;
                const MAX_HEIGHT = 600;
                let width = img.width;
                let height = img.height;

                if (width > MAX_WIDTH) {
                    height = height * (MAX_WIDTH / width);
                    width = MAX_WIDTH;
                }
                if (height > MAX_HEIGHT) {
                    width = width * (MAX_HEIGHT / height);
                    height = MAX_HEIGHT;
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        const newFile = new File([blob], file.name, {type: "image/jpeg"});
                        setNewImageFile(newFile);
                        setCurrentImage(URL.createObjectURL(newFile));
                    },
                    "image/jpeg",
                    0.7
                );
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };

    const resetForm = () => {
        setTemplateText("");
        setType(null);
        setPoints(0);
        setGivePoints(false);
        setCurrentImage(null);
        setNewImageFile(null);
        setBranchId("");
        setSendToAllBranches(false);
        setSelectedDate(null);
        setIntervalValue("");
        setIntervalUnit("DAYS");
        setDaysBefore("");
        setDaysAfterFirstAppointment("");
        setDaysAfterLastAppointment("");
        setAppointmentsForPeriod("");
        setPeriodType("YEAR");
        setMinutesBeforeAppointment(30);
        setNotifyBeforeDays(3);
        setGender("");
        setMinAge("");
        setMaxAge("");
    };

    return (
        <>
            <Box sx={{display: "flex", gap: 2, mb: 2}}>
                {
                    editingId ? (
                        <Typography>Тип: {typeLabels[type]}</Typography>
                    ) : (
                        <TextField
                            select
                            label="Тип"
                            value={type}
                            onChange={(e) => {
                                setType(e.target.value);
                                if (e.target.value === "REMIND_APPOINTMENT") {
                                    setGivePoints(false);
                                }
                            }}
                            sx={{minWidth: 150}}
                        >
                            {Object.keys(typeLabels).map((key) => (
                                <MenuItem key={key} value={key}>
                                    {typeLabels[key]}
                                </MenuItem>
                            ))}
                        </TextField>
                    )
                }

            </Box>

            <TextField
                label="Текст сообщения"
                multiline
                rows={4}
                fullWidth
                value={templateText}
                onChange={(e) => setTemplateText(e.target.value.slice(0, 800))}
                helperText={`${templateText.length}/800 символов`}
                sx={{mb: 2}}
            />

            {type !== "REMIND_APPOINTMENT" && (
                <>
                    {currentImage && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <img
                                src={currentImage}
                                alt="Превью"
                                style={{
                                    maxWidth: "100%",
                                    height: 200,
                                    objectFit: "contain",
                                    marginBottom: 16,
                                }}
                            />
                        </Box>
                    )}

                    {type !== "PROMO_END" && (
                        <>
                            <input
                                accept="image/*"
                                type="file"
                                id="upload-image"
                                hidden
                                onChange={handleImageChange}
                            />
                            <label htmlFor="upload-image">
                                <Button variant="outlined" component="span" sx={{my: 2}}>
                                    {currentImage ? "Заменить изображение" : "Добавить изображение"}
                                </Button>
                            </label>
                        </>
                    )}
                </>
            )}

            <Box sx={{display: "flex", flexDirection: "column", gap: 2, mb: 2}}>
                {type === "DATE" && (
                    <Box sx={{display: "flex", gap: 2, mb: 2}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isRepeatable}
                                    onChange={(e) => setRepeatable(e.target.checked)}
                                />
                            }
                            label="Повторять"
                        />

                        <TextField
                            label="Дата"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{shrink: true}}
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />

                        {
                            isRepeatable && (
                                <>

                                    <TextField
                                        label="Интервал (в числах)"
                                        type="number"
                                        value={intervalValue}
                                        onChange={(e) => setIntervalValue(e.target.value)}
                                    />
                                    <FormControl sx={{minWidth: 120}}>
                                        <InputLabel>Единица</InputLabel>
                                        <Select
                                            value={intervalUnit}
                                            onChange={(e) => setIntervalUnit(e.target.value)}
                                            label="Единица"
                                        >
                                            <MenuItem value="DAYS">Дни</MenuItem>
                                            <MenuItem value="WEEKS">Недели</MenuItem>
                                            <MenuItem value="MONTHS">Месяцы</MenuItem>
                                            <MenuItem value="YEARS">Годы</MenuItem>
                                        </Select>
                                    </FormControl>
                                </>
                            )
                        }
                    </Box>
                )}

                {type === "DATE" && (
                    <FormControl sx={{minWidth: 200}}>
                        <InputLabel>Пол пользователя</InputLabel>
                        <Select
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                            label="Пол пользователя"
                        >
                            <MenuItem value="">Все</MenuItem>
                            <MenuItem value="male">Мужской</MenuItem>
                            <MenuItem value="female">Женский</MenuItem>
                        </Select>
                    </FormControl>
                )}
                {type === "DATE" && (
                    <Box sx={{display: "flex", gap: 2}}>
                        <TextField
                            label="Минимальный возраст"
                            type="number"
                            value={minAge}
                            onChange={(e) => setMinAge(e.target.value)}
                            inputProps={{min: 0}}
                            error={!validateDateAgeRange()}
                            helperText={!validateDateAgeRange() ? "Минимальный возраст должен быть не больше максимального" : "Граница включительная; без даты рождения тоже получат"}
                        />
                        <TextField
                            label="Максимальный возраст"
                            type="number"
                            value={maxAge}
                            onChange={(e) => setMaxAge(e.target.value)}
                            inputProps={{min: 0}}
                            error={!validateDateAgeRange()}
                            helperText={!validateDateAgeRange() ? "Максимальный возраст должен быть не меньше минимального" : "Граница включительная; без даты рождения тоже получат"}
                        />
                    </Box>
                )}

                {type === "BIRTHDAY" && (
                    <TextField
                        label="Дней до дня рождения"
                        type="number"
                        value={daysBefore}
                        onChange={(e) => setDaysBefore(e.target.value)}
                    />
                )}

                {type === "FIRST_APPOINTMENT" && (
                    <TextField
                        label="Дней после первого приёма"
                        type="number"
                        value={daysAfterFirstAppointment}
                        onChange={(e) => setDaysAfterFirstAppointment(e.target.value)}
                    />
                )}

                {type === "INACTIVE_PATIENT" && (
                    <TextField
                        label="Дней после последнего визита"
                        type="number"
                        value={daysAfterLastAppointment}
                        onChange={(e) => setDaysAfterLastAppointment(e.target.value)}
                    />
                )}

                {type === "REGULAR_PATIENT" && (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField
                            label="Приёмов за период"
                            type="number"
                            value={appointmentsForPeriod}
                            onChange={(e) => setAppointmentsForPeriod(e.target.value)}
                        />
                        <FormControl sx={{minWidth: 150}}>
                            <InputLabel>Тип периода</InputLabel>
                            <Select
                                value={periodType}
                                onChange={(e) => setPeriodType(e.target.value)}
                                label="Тип периода"
                            >
                                <MenuItem value="HALF_YEAR">Пол года</MenuItem>
                                <MenuItem value="YEAR">Год</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {type === "DRINKS_BEFORE_APPOINTMENT" && (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField
                            label="За сколько минут до приема предлагать?"
                            type="number"
                            value={minutesBeforeAppointment}
                            onChange={(e) => setMinutesBeforeAppointment(parseInt(e.target.value))}
                        />
                    </Box>
                )}

                {type === "PROMO_END" && (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField
                            label="За сколько дней до окончания акции оповещать?"
                            type="number"
                            value={notifyBeforeDays}
                            onChange={(e) => setNotifyBeforeDays(parseInt(e.target.value))}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{display: "flex", gap: 2, mb: 2}}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendToAllBranches}
                            onChange={(e) => setSendToAllBranches(e.target.checked)}
                        />
                    }
                    label="Для всех филиалов"
                />

                {!sendToAllBranches && (
                    <FormControl sx={{minWidth: 200}}>
                        <InputLabel>Филиал</InputLabel>
                        <Select
                            value={branchId}
                            onChange={(e) => setBranchId(e.target.value)}
                            label="Филиал"
                        >
                            {availableBranches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            <Box sx={{display: "flex", gap: 2, mb: 2}}>
                {type !== "REMIND_APPOINTMENT" && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={givePoints}
                                onChange={(e) => setGivePoints(e.target.checked)}
                            />
                        }
                        label="Начислять баллы"
                    />
                )}

                {givePoints && (
                    <TextField
                        label="Количество баллов"
                        type="number"
                        value={points}
                        onChange={(e) => setPoints(Math.max(0, Number(e.target.value)))}
                    />
                )}
            </Box>

            <Box sx={{display: "flex", justifyContent: "flex-end", gap: 2}}>
                <Button onClick={onCloseModal} color="error">
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={(!sendToAllBranches && !branchId) || !templateText || !type || !validateDateAgeRange()}
                >
                    {editingId ? "Обновить" : "Создать"}
                </Button>
            </Box>
        </>
    );
};

export default TriggerForm;
